import {
  AppCard,
  ChipList,
  Icon,
  PageContainer,
  RouteButtonLink,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiAccount, mdiEye, mdiPlus } from "@mdi/js";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { ApiListResponse } from "../../../@types/services/Api";
import type {
  AssignmentRuleFilter,
  AssignmentRuleIndex,
} from "../../../@types/services/AssignmentRule";
import { UserInfoBadge } from "../../../components/molecules/UserInfoBadge";
import { ForbiddenError } from "../../../errors/ForbiddenError";
import { fetchAssignmentRules } from "../../../services/assignmentRules";
import { AssignmentRulesFilters } from "./-internal-components/filters";

const AssignmentRuleTableRow: React.FC<{ rule: AssignmentRuleIndex }> = ({
  rule,
}) => {
  const { t } = useTranslation();

  return (
    <TableRow key={rule.id}>
      <TableCell>
        <UserInfoBadge
          avatarIcon={<Icon icon={mdiAccount} />}
          avatarType="icon"
          info={rule.user.full_name}
          photoUser={rule.user}
          subInfo={rule.user.login}
        />
      </TableCell>
      <TableCell>
        <ChipList
          fieldLabel={t("assignmentRules.table.reasons")}
          items={rule.mm_request_reasons.map((reason) => ({
            id: reason.mm_request_reason.id,
            label: reason.mm_request_reason.title,
          }))}
          limit={2}
        />
      </TableCell>
      <TableCell>
        <ChipList
          fieldLabel={t("assignmentRules.table.plantFields")}
          items={rule.plant_fields.map((field) => ({
            id: field.plant_field.id,
            label: field.plant_field.name,
          }))}
          limit={2}
        />
      </TableCell>
      <TableCell>
        <ChipList
          fieldLabel={t("assignmentRules.table.requesterAreas")}
          items={rule.requester_areas.map((area) => ({
            id: area.requester_area.id,
            label: area.requester_area.name,
          }))}
          limit={2}
        />
      </TableCell>
      <TableCell>
        <ChipList
          fieldLabel={t("assignmentRules.table.plants")}
          items={rule.plants.map((plant) => ({
            id: plant.plant.id,
            label: plant.plant.plant_number.toString(),
            modalLabel: plant.plant.plant_number_with_name,
          }))}
          limit={2}
        />
      </TableCell>
      <TableCell align="right">
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            justifyContent: "flex-end",
          }}
        >
          <Tooltip title={t("assignmentRules.tooltip.viewButton")}>
            <RouteIconButtonLink
              params={{ assignmentRuleId: rule.id }}
              to="/mm_request_assignment_rules/$assignmentRuleId"
            >
              <Icon icon={mdiEye} />
            </RouteIconButtonLink>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const AssignmentRulesTable: React.FC = () => {
  const navigate = Route.useNavigate();
  const { t } = useTranslation();

  const {
    rowsPerPage = 25,
    page = 1,
    user,
    reasons,
    plantFields,
    requesterAreas,
    plants,
  } = Route.useSearch();

  // Create filters object from URL search parameters
  const filters: AssignmentRuleFilter = useMemo(
    () => ({
      mm_request_reason_id: reasons || [],
      plant_field_id: plantFields || [],
      plant_id: plants || [],
      requester_area_id: requesterAreas || [],
      user_id: user || null,
    }),
    [user, reasons, plantFields, requesterAreas, plants],
  );

  const {
    data: rulesResponse,
    isLoading: isLoadingRules,
    isFetching: isFetchingRules,
  } = useQuery<
    ApiListResponse<AssignmentRuleIndex>,
    Error,
    ApiListResponse<AssignmentRuleIndex>
  >({
    placeholderData: (previousData?: ApiListResponse<AssignmentRuleIndex>) =>
      previousData,
    queryFn: () =>
      fetchAssignmentRules({
        filters,
        items_per_page: rowsPerPage,
        page,
      }),
    queryKey: ["mmRequestAssignmentRules", "list", filters, page, rowsPerPage],
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (!rulesResponse) return;

    const { current_page: currentPage, items_per_page: currentItemsPerPage } =
      rulesResponse;

    if (currentPage !== page || currentItemsPerPage !== rowsPerPage) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          page: currentPage,
          rowsPerPage: currentItemsPerPage,
        }),
      });
    }
  }, [page, rowsPerPage, navigate, rulesResponse]);

  const rules = rulesResponse?.results || [];

  const handleChangePage = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      newPage: number,
    ): void => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: newPage + 1,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        }),
      });
    },
    [navigate],
  );

  return (
    <AppCard>
      <AssignmentRulesFilters
        actionButton={
          <RouteButtonLink
            color="primary"
            startIcon={<Icon icon={mdiPlus} />}
            to="/mm_request_assignment_rules/new"
            variant="contained"
          >
            {t("assignmentRules.buttons.createNewRule")}
          </RouteButtonLink>
        }
        isFetching={isFetchingRules}
      />
      <Box sx={{ overflowX: "auto" }}>
        <Table aria-label="tabela de regras de atribuição">
          <TableHead>
            <TableRow>
              <TableCell>{t("assignmentRules.table.name")}</TableCell>
              <TableCell>{t("assignmentRules.table.reasons")}</TableCell>
              <TableCell>{t("assignmentRules.table.plantFields")}</TableCell>
              <TableCell>{t("assignmentRules.table.requesterAreas")}</TableCell>
              <TableCell>{t("assignmentRules.table.plants")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!!isLoadingRules && (
              <TableRow>
                <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}

            {!isLoadingRules &&
              rules &&
              rules.length > 0 &&
              rules.map((rule) => (
                <AssignmentRuleTableRow key={rule.id} rule={rule} />
              ))}

            {!isLoadingRules && (!rules || rules.length === 0) && (
              <TableRow>
                <TableCell align="center" colSpan={6}>
                  {t("assignmentRules.table.noRulesFound")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={rulesResponse?.total_items || 0}
          labelDisplayedRows={({ from, to, count }) =>
            t("assignmentRules.table.displayedRows", {
              count,
              from,
              to,
            })
          }
          labelRowsPerPage={t("assignmentRules.table.itemsPerPage")}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </AppCard>
  );
};

const AssignmentRules: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer maxWidth="xl" title={t("assignmentRules.titles.index")}>
      <AssignmentRulesTable />
    </PageContainer>
  );
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private/mm_request_assignment_rules/")({
  component: AssignmentRules,
  validateSearch: z.object({
    page: z.optional(z.coerce.number()).catch(undefined),
    plantFields: z.optional(z.array(z.string())).catch([]),
    plants: z.optional(z.array(z.string())).catch([]),
    reasons: z.optional(z.array(z.string())).catch([]),
    requesterAreas: z.optional(z.array(z.string())).catch([]),
    rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
    user: z.optional(z.coerce.number()).catch(undefined),
  }),
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Assignment Rules - View")) {
      return;
    }

    throw new ForbiddenError();
  },
});
